import { Flex, Form } from 'antd';
import type { Moment } from 'moment';
import ModalStaff, { useModalStaffRef } from 'QuickBooking/ModalStaff';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import aptActions from 'services/actions/apt';
import aptSelectors from 'services/selectors/apt';
import shopSelectors from 'services/selectors/shop';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { ICustomerUI } from 'types/customer';
import { IStaffUI } from 'types/staff';
import { formatPhone } from 'utils/formatPhone';
import { isBookableStaff } from 'utils/staff';
import toast from 'utils/toast';
import uuid from 'utils/uuid';
import { buildAssignStaffPool, resolvePreferredStaff } from './assign-staff-pool';
import Modal from './Modal';
import StaffAssignItem, { IStaffAssignItem } from './StaffAssignItem';

export type { IStaffAssignItem };

/**
 * Du lieu cua lan dat lich dang mo, do phia goi cung cap.
 *
 * Modal khong doc signal nao ca: man dat lich thuong va Quick Booking giu state o hai noi
 * khac nhau, nen ca hai deu phai tu dich state cua minh sang shape nay.
 */
export type IGroupSource = {
  customer: ICustomerUI | null;
  /** Thoi diem bat dau, dung chung cho moi lich trong nhom. */
  time: Moment | null | undefined;
  /** Do dai mot lich, tinh bang phut. */
  durationMinutes: number;
  serviceNames: string[];
  /** Tho da chon truoc do; duoc xep vao the dau tien. */
  preferredStaffId?: string | null;
  /** Gia tri Request Staff ma moi the nhan luc mo; sau do tung the tu quyet dinh. */
  defaultRequestStaff: boolean;
};

type Ref = {
  open: (totalPeople: number, source: IGroupSource) => void;
  close: () => void;
};
export const useModalConfirmGroupRef = () => useRef<Ref>(null);
type Props = {
  onOk: (staffAssigns: IStaffAssignItem[]) => void;
  onCancel?: () => void;
  zIndex?: number;
};

const ModalConfirmGroup = forwardRef<Ref, Props>(({ onOk, onCancel = () => undefined, zIndex }, ref) => {
  const dispatch = useAppDispatch();
  const staffPicker = useModalStaffRef();
  const [visible, setVisible] = useState(false);
  const [source, setSource] = useState<IGroupSource | null>(null);
  const staffOrder = shopSelectors.staff();
  // Filter the available-booking list, not the order list: the latter is only
  // used to sort, while this one is what the picker actually offers.
  const _all = aptSelectors.staffAvailableBooking();
  const _staffs = useMemo(() => _all.filter(isBookableStaff), [_all]);
  const loading = aptSelectors.staffAvailableBookingLoading();

  const staffs = useMemo(() => {
    const list: IStaffUI[] = [];
    staffOrder.map(o => {
      const item = _staffs.find(s => s.id === o.id);
      if (item) {
        list.push(item);
      }
    });
    return list;
  }, [_staffs, staffOrder]);

  const servicesText = useMemo(() => (source?.serviceNames || []).join(', '), [source]);

  const time = useMemo(() => {
    const startTime = source?.time?.format('hh:mm A') || '';
    const endTime = source?.time?.clone().add(source?.durationMinutes || 0, 'minute')?.format('hh:mm A') || '';
    return `${startTime} - ${endTime}`;
  }, [source]);

  const customer = useMemo(() => {
    if (!source?.customer) return '';
    return `${source.customer.name} - ${formatPhone(source.customer.phone)}`;
  }, [source]);

  const [form] = Form.useForm();
  /**
   * Lan xep the dang cho danh sach tho ranh.
   *
   * Nguoi goi ban yeu cau lay tho ranh ngay truoc khi mo modal, ma yeu cau do lam rong danh
   * sach cho toi khi co ket qua. Xep the ngay luc mo se doc phai danh sach rong va roi het
   * ve thu tu tho cua tiem — dung ca y nghia cua buoc nay. Vi vay giu lai yeu cau va chi xep
   * khi danh sach da lang.
   */
  const pendingPrefill = useRef<{ totalPeople: number; source: IGroupSource } | null>(null);

  const onOpen: Ref['open'] = (totalPeople, nextSource) => {
    if (!totalPeople) return;
    setSource(nextSource);
    setVisible(true);
    form.setFieldValue('totalPeople', totalPeople);
    form.setFieldValue('staffAssigns', []);
    pendingPrefill.current = { totalPeople, source: nextSource };
  };

  useEffect(() => {
    const pending = pendingPrefill.current;
    if (!pending || !visible || loading) return;
    pendingPrefill.current = null;
    const preferred = resolvePreferredStaff(pending.source.preferredStaffId, staffs, staffOrder);
    const pool = buildAssignStaffPool(preferred, staffs, staffOrder, pending.totalPeople);
    // Moi lich trong nhom thua co Request Staff da chon o buoc truoc; sau do tung the tu quyet dinh.
    form.setFieldValue('staffAssigns', pool.map(o => ({
      uuid: uuid(),
      staff: o,
      requestStaff: pending.source.defaultRequestStaff,
    })));
  }, [visible, loading, staffs, staffOrder, form]);

  const onClose = () => {
    pendingPrefill.current = null;
    setVisible(false);
    onCancel();
  };
  const handleOk = () => {
    const staffAssigns: IStaffAssignItem[] = form.getFieldValue('staffAssigns');
    // Khong con tho nao de gan thi khong co lich nao duoc tao. Dong modal im lang o day se
    // lam nhan vien tuong da dat xong.
    if (!staffAssigns?.length) {
      toast.info('No staff available for this time');
      return;
    }
    onOk(staffAssigns);
    setVisible(false);
  };

  const handleOnChangeStaff = (onChange: (staff: IStaffUI) => void) => {
    const startTime = source?.time?.format('MM-DD-YYYY HH:mm') || '';
    const endTime = source?.time?.clone().add(source?.durationMinutes || 0, 'minute')?.format('MM-DD-YYYY HH:mm') || '';
    dispatch(aptActions.staffAvailableBooking.fetch({ startTime, endTime }));
    staffPicker.current?.openCallback(onChange);
  };

  useImperativeHandle(ref, () => ({
    open: onOpen,
    close: onClose,
  }));

  return (
    <Modal cancelText='BACK' okText='BOOK APPOINTMENT' open={visible} title="ASSIGN STAFF" onCancel={onClose} onOk={handleOk} width='1200px' height='auto' zIndex={zIndex} loading={loading}>
      <Container form={form}>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            // Tiem co the it tho hon so khach da bam. Hien so the that su duoc gan de nhan vien
            // thay ngay minh sap dat may lich, thay vi con so da yeu cau.
            const assigned = (getFieldValue('staffAssigns') || []).length;
            return <div className='title-section'>Group {assigned || getFieldValue('totalPeople')} People</div>;
          }}
        </Form.Item>
        <Flex gap={'16px'} align='center'>
          <div className='title-section'>Time: {source?.time?.format('MM-DD-YYYY')} </div>
          <div className='title-section'>{time}</div>
        </Flex>
        <Form.List name={'staffAssigns'}>
          {(fields) =>
            <StaffAssignsStyled>
              {fields.map(o => (
                <Form.Item key={o.key} name={o.name}>
                  <StaffAssignItem onChangeStaff={handleOnChangeStaff} servicesText={servicesText} time={time} customer={customer} />
                </Form.Item>
              ))}
            </StaffAssignsStyled>
          }
        </Form.List>
      </Container>
      <ModalStaff loading={loading} title='CHOOSE STAFF' ref={staffPicker} staffList={staffs} />
    </Modal>
  );
});
ModalConfirmGroup.displayName = 'ModalConfirmGroup';
export default ModalConfirmGroup;


const Container = styled(Form)`
  padding: 0 16px;
  padding-top: 16px;
  .title-section {
    color: #1D2129;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    margin-bottom: 8px;
}
`;

const StaffAssignsStyled = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr 1fr 1fr 1fr;
`;
